import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { ApolloError, useApolloClient, useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { SelectOrganization, UpdateOrganizations } from '@actions/organization';
import {
  acceptOrganizationInvitationMutation,
  declineOrganizationInvitationMutation,
} from '@graphql/mutations/organization';
import { pendingOrganizationInvitationsQuery } from '@graphql/queries/organization';
import {
  applyActiveOrgToken,
  getValidSessionToken,
  subscribeSessionToken,
} from '@services/sessionToken';
import { ErrorCategory, handleError } from '@services/errorService';
import type { Organization } from '@app-types/index';

export interface PendingOrganizationInvitation {
  invitationId: string;
  organizationId: string;
  organizationName: string;
  organizationAvatar?: { url?: string | null } | null;
  roleType: string;
  invitedAt?: string | null;
}

interface PendingInvitationsData {
  pendingOrganizationInvitations: PendingOrganizationInvitation[];
}

interface AcceptInvitationData {
  acceptOrganizationInvitation: Organization;
}

interface UseOrganizationInvitationsReturn {
  invitations: PendingOrganizationInvitation[];
  loading: boolean;
  busyInvitationId: string | null;
  acceptInvitation: (invitationId: string) => Promise<Organization | null>;
  declineInvitation: (invitationId: string) => Promise<boolean>;
  refetch: () => Promise<unknown>;
}

export const useOrganizationInvitations = (
  enabled = true,
): UseOrganizationInvitationsReturn => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [busyInvitationId, setBusyInvitationId] = useState<string | null>(null);
  const busyRef = useRef(false);
  const token = useSyncExternalStore(
    subscribeSessionToken,
    getValidSessionToken,
    () => null,
  );
  const [rejectedToken, setRejectedToken] = useState<string | null>(null);
  const canQuery = enabled && !!token && token !== rejectedToken;
  const client = useApolloClient();
  const requestId = useRef(0);
  const [queryState, setQueryState] = useState({
    token: null as string | null,
    invitations: [] as PendingOrganizationInvitation[],
    loading: false,
  });
  const refetch = useCallback(async (): Promise<unknown> => {
    if (!canQuery || !token || getValidSessionToken() !== token) return null;
    const id = ++requestId.current;
    setQueryState((state) => ({ ...state, loading: true }));
    try {
      const result = await client.query<PendingInvitationsData>({
        query: pendingOrganizationInvitationsQuery,
        fetchPolicy: 'network-only',
      });
      if (id === requestId.current && getValidSessionToken() === token) {
        setQueryState({
          token,
          invitations: result.data.pendingOrganizationInvitations,
          loading: false,
        });
      }
      return result;
    } catch (error) {
      if (
        id === requestId.current &&
        getValidSessionToken() === token &&
        error instanceof ApolloError &&
        (error.graphQLErrors.some(
          ({ message, extensions }) =>
            extensions?.code === 'UNAUTHENTICATED' ||
            /authentication required|token expired|invalid token/i.test(
              message,
            ),
        ) ||
          (error.networkError as { statusCode?: number } | null)?.statusCode ===
            401)
      )
        setRejectedToken(token);
      return null;
    } finally {
      if (id === requestId.current)
        setQueryState((state) => ({ ...state, loading: false }));
    }
  }, [canQuery, client, token]);
  useEffect(() => {
    if (!canQuery) return undefined;
    void refetch();
    // Check the current session before each request. Apollo 3.0 watchQuery can
    // reissue a network-only query while skipping or resubscribing after errors.
    const timer = setInterval(() => {
      void refetch();
    }, 60_000);
    return () => {
      clearInterval(timer);
      requestId.current += 1;
    };
  }, [canQuery, refetch]);
  const [accept] = useMutation<AcceptInvitationData>(
    acceptOrganizationInvitationMutation,
  );
  const [decline] = useMutation<{ declineOrganizationInvitation: boolean }>(
    declineOrganizationInvitationMutation,
  );

  const acceptInvitation = useCallback(
    async (invitationId: string): Promise<Organization | null> => {
      if (busyRef.current) return null;
      busyRef.current = true;
      setBusyInvitationId(invitationId);
      try {
        const { data: result } = await accept({ variables: { invitationId } });
        const organization = result?.acceptOrganizationInvitation;
        if (!organization) return null;

        dispatch(UpdateOrganizations(organization));
        dispatch(SelectOrganization(organization));
        if (organization.token) applyActiveOrgToken(organization.token);
        await refetch();
        navigate('/home', {
          replace: true,
          state: { orgSwitchKey: organization._id },
        });
        return organization;
      } catch (error) {
        handleError(error, { category: ErrorCategory.GRAPHQL });
        return null;
      } finally {
        busyRef.current = false;
        setBusyInvitationId(null);
      }
    },
    [accept, dispatch, navigate, refetch],
  );

  const declineInvitation = useCallback(
    async (invitationId: string): Promise<boolean> => {
      if (busyRef.current) return false;
      busyRef.current = true;
      setBusyInvitationId(invitationId);
      try {
        const { data: result } = await decline({ variables: { invitationId } });
        if (!result?.declineOrganizationInvitation) return false;
        await refetch();
        return true;
      } catch (error) {
        handleError(error, { category: ErrorCategory.GRAPHQL });
        return false;
      } finally {
        busyRef.current = false;
        setBusyInvitationId(null);
      }
    },
    [decline, refetch],
  );

  return {
    invitations:
      canQuery && queryState.token === token ? queryState.invitations : [],
    loading: canQuery && queryState.loading,
    busyInvitationId,
    acceptInvitation,
    declineInvitation,
    refetch,
  };
};

export default useOrganizationInvitations;
